import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    CircularProgress,
    IconButton,
    Tooltip,
    Card,
    CardHeader,
    CardContent,
    List,
    ListItem,
    ListItemText,
    Divider
} from '@mui/material';
import {
    Download,
    Save,
    Delete,
    Email
} from '@mui/icons-material';
import Loader from '../Loader';
import authService from '../api-authorization/AuthorizeService';
import { useToast } from '../../hooks/useToast';
import { ToastNotification } from '../common/ToastNotification';
import { formatApiError } from '../../utils/errorFormatter';

interface ReportTemplate {
    id: number;
    name: string;
    description: string;
    category: string;
    supportedFormats: number;
    sortOrder: number;
    isActive: boolean;
}

interface ReportPreset {
    id: number;
    reportTemplateId: number;
    name: string;
    description: string;
    parameters: string;
    createdAt: string;
}

interface Group {
    id: number;
    name: string;
}

const ReportOutputFormat = {
    XML: 1,
    XLSX: 2,
    PDF: 4
};

const parseSupportedFormats = (formats: any): number => {
    if (typeof formats === 'number') return formats;
    if (typeof formats !== 'string') return 0;
    return formats.split(',').reduce((acc, format) => {
        const key = format.trim().toUpperCase() as keyof typeof ReportOutputFormat;
        if (ReportOutputFormat[key]) return acc | ReportOutputFormat[key];
        return acc;
    }, 0);
};

export const ReportsPage: React.FC = () => {
    const [templates, setTemplates] = useState<ReportTemplate[]>([]);
    const [presets, setPresets] = useState<ReportPreset[]>([]);
    const [groups, setGroups] = useState<Group[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<number | ''>('');
    const [selectedGroup, setSelectedGroup] = useState<number | ''>('');
    const [startDate, setStartDate] = useState<string>(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [format, setFormat] = useState<number>(ReportOutputFormat.XLSX);
    const [email, setEmail] = useState<string>('');
    const [generatingReport, setGeneratingReport] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);

    const { toast, showError, showSuccess, handleClose } = useToast();

    useEffect(() => {
        const loadInitialData = async () => {
            setPageLoading(true);
            try {
                const user = await authService.getUser();

                // Получаем email из профиля пользователя
                const userEmail = (user?.profile as any)?.email
                    || (user?.profile as any)?.name
                    || (user?.profile as any)?.preferred_username
                    || '';

                setEmail(userEmail);

                await Promise.all([loadTemplates(), loadPresets(), loadGroups()]);
            } catch (err) {
                showError('Ошибка при загрузке начальных данных: ' + (err as Error).message);
            } finally {
                setPageLoading(false);
            }
        };
        loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (groups.length > 0 && selectedGroup === '') {
            setSelectedGroup(groups[0].id);
        }
    }, [groups, selectedGroup]);

    const loadTemplates = async () => {
        try {
            const response = await fetch('/api/reports/templates');
            if (!response.ok) {
                const errorMsg = await formatApiError(response, 'Не удалось загрузить шаблоны отчетов');
                throw new Error(errorMsg);
            }
            const data = await response.json();
            setTemplates(data.map((t: any) => ({ ...t, supportedFormats: parseSupportedFormats(t.supportedFormats) })));
        } catch (err) {
            showError('Ошибка загрузки шаблонов отчетов: ' + (err as Error).message);
        }
    };

    const loadPresets = async () => {
        try {
            const response = await fetch('/api/reports/presets');
            if (!response.ok) {
                const errorMsg = await formatApiError(response, 'Не удалось загрузить пресеты');
                throw new Error(errorMsg);
            }
            setPresets(await response.json());
        } catch (err) {
            showError('Ошибка загрузки пресетов: ' + (err as Error).message);
        }
    };

    const loadGroups = async () => {
        try {
            const response = await fetch('/api/reports/groups');
            if (!response.ok) {
                const errorMsg = await formatApiError(response, 'Не удалось загрузить группы');
                throw new Error(errorMsg);
            }
            setGroups(await response.json());
        } catch (err) {
            showError('Ошибка загрузки групп: ' + (err as Error).message);
        }
    };

    const handleGenerateReport = async () => {
        if (!selectedTemplate) {
            showError('Пожалуйста, выберите шаблон отчета');
            return;
        }
        setGeneratingReport(true);
        try {
            const response = await fetch('/api/reports/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    reportTemplateId: selectedTemplate,
                    groupId: selectedGroup || null,
                    startDate: new Date(startDate).toISOString(),
                    endDate: (() => { const d = new Date(endDate); d.setDate(d.getDate() + 1); return d.toISOString(); })(),
                    format: format,
                    async: false
                })
            });
            if (!response.ok) {
                const errorMsg = await formatApiError(response, 'Не удалось создать отчет');
                throw new Error(errorMsg);
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const template = templates.find(t => t.id === selectedTemplate);
            const extension = format === ReportOutputFormat.XML ? 'xml' : format === ReportOutputFormat.XLSX ? 'xlsx' : 'pdf';
            a.download = `${template?.name}_${startDate}_${endDate}.${extension}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            showSuccess('Отчет успешно создан и загружен');
        } catch (err) {
            showError('Ошибка при создании отчета: ' + (err as Error).message);
        } finally {
            setGeneratingReport(false);
        }
    };

    const handleSendReportToEmail = async () => {
        if (!selectedTemplate) {
            showError('Пожалуйста, выберите шаблон отчета');
            return;
        }
        if (!email || !email.includes('@')) {
            showError('Пожалуйста, введите корректный email адрес');
            return;
        }
        setSendingEmail(true);
        try {
            const response = await fetch('/api/reports/send-email', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    reportTemplateId: selectedTemplate,
                    groupId: selectedGroup || null,
                    startDate: new Date(startDate).toISOString(),
                    endDate: (() => { const d = new Date(endDate); d.setDate(d.getDate() + 1); return d.toISOString(); })(),
                    format: format,
                    email: email
                })
            });
            if (!response.ok) {
                const errorMsg = await formatApiError(response, 'Не удалось отправить отчет');
                throw new Error(errorMsg);
            }
            const result = await response.json();
            showSuccess(result.message || `Отчет успешно отправлен на ${email}`);
        } catch (err) {
            showError('Ошибка при отправке отчета: ' + (err as Error).message);
        } finally {
            setSendingEmail(false);
        }
    };

    const handleSavePreset = async () => {
        if (!selectedTemplate) return;
        const presetName = prompt('Введите название пресета:');
        if (!presetName) return;
        try {
            const response = await fetch('/api/reports/presets', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    reportTemplateId: selectedTemplate,
                    name: presetName,
                    description: `Пресет для ${templates.find(t => t.id === selectedTemplate)?.name}`,
                    parameters: JSON.stringify({ startDate, endDate, format, groupId: selectedGroup })
                })
            });
            if (!response.ok) {
                const errorMsg = await formatApiError(response, 'Не удалось сохранить пресет');
                throw new Error(errorMsg);
            }
            showSuccess('Пресет успешно сохранен');
            loadPresets();
        } catch (err) {
            showError('Ошибка при сохранении пресета: ' + (err as Error).message);
        }
    };

    const loadPreset = (preset: ReportPreset) => {
        const params = JSON.parse(preset.parameters);
        setSelectedTemplate(preset.reportTemplateId);
        setStartDate(params.startDate);
        setEndDate(params.endDate);
        setFormat(params.format);
        setSelectedGroup(params.groupId || '');
    };

    const deletePreset = async (presetId: number) => {
        try {
            const response = await fetch(`/api/reports/presets/${presetId}`, { method: 'DELETE' });
            if (!response.ok) {
                const errorMsg = await formatApiError(response, 'Не удалось удалить пресет');
                throw new Error(errorMsg);
            }
            showSuccess('Пресет успешно удален');
            loadPresets();
        } catch (err) {
            showError('Ошибка при удалении пресета: ' + (err as Error).message);
        }
    };

    const getFormatName = (formatValue: number) => {
        if (formatValue === ReportOutputFormat.XML) return 'Excel XML';
        if (formatValue === ReportOutputFormat.XLSX) return 'Excel (XLSX)';
        if (formatValue === ReportOutputFormat.PDF) return 'PDF';
        return 'Unknown';
    };

    const isFormatSupported = (templateId: number, formatValue: number) => {
        const template = templates.find(t => t.id === templateId);
        return template ? (template.supportedFormats & formatValue) !== 0 : false;
    };

    const groupedTemplates = templates.reduce((acc, template) => {
        if (!acc[template.category]) acc[template.category] = [];
        acc[template.category].push(template);
        return acc;
    }, {} as Record<string, ReportTemplate[]>);

    if (pageLoading) return <Loader />;

    return (
        <Box>
            <Card>
                <CardHeader title="Отчеты" />
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <Typography variant="h6" gutterBottom>Создать отчет</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Шаблон отчета</InputLabel>
                                        <Select value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value as number)} label="Шаблон отчета">
                                            {Object.entries(groupedTemplates).flatMap(([category, temps]) => [
                                                <MenuItem key={`cat-${category}`} disabled><strong>{category}</strong></MenuItem>,
                                                ...temps.map(t => <MenuItem key={t.id} value={t.id}>&nbsp;&nbsp;{t.name}</MenuItem>)
                                            ])}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Группа</InputLabel>
                                        <Select value={selectedGroup} onChange={(e) => setSelectedGroup(e.target.value as number)} label="Группа">
                                            <MenuItem value=""><em>По умолчанию</em></MenuItem>
                                            {groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {selectedTemplate && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">{templates.find(t => t.id === selectedTemplate)?.description}</Typography>
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Дата начала" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Дата окончания" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Формат вывода</InputLabel>
                                        <Select value={format} onChange={(e) => setFormat(e.target.value as number)} label="Формат вывода" disabled={!selectedTemplate}>
                                            {[ReportOutputFormat.XLSX, ReportOutputFormat.PDF, ReportOutputFormat.XML].map(fmt => (
                                                <MenuItem key={fmt} value={fmt} disabled={selectedTemplate ? !isFormatSupported(selectedTemplate as number, fmt) : false}>
                                                    {getFormatName(fmt)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Email для отправки отчета"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="example@email.com"
                                        helperText="Введите email для отправки отчета"
                                        disabled={sendingEmail}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Button variant="contained" startIcon={generatingReport ? <CircularProgress size={20} color="inherit" /> : <Download />} onClick={handleGenerateReport} disabled={generatingReport || sendingEmail || !selectedTemplate} fullWidth>
                                        {generatingReport ? 'Создание...' : 'Скачать отчет'}
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Button variant="contained" color="secondary" startIcon={sendingEmail ? <CircularProgress size={20} color="inherit" /> : <Email />} onClick={handleSendReportToEmail} disabled={sendingEmail || generatingReport || !selectedTemplate || !email} fullWidth>
                                        {sendingEmail ? 'Отправка...' : 'Отправить на email'}
                                    </Button>
                                </Grid>
                                <Grid item xs={12}>
                                    <Button variant="outlined" startIcon={<Save />} onClick={handleSavePreset} disabled={!selectedTemplate || generatingReport || sendingEmail} fullWidth>
                                        Сохранить пресет
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6" gutterBottom sx={{ mt: { xs: 3, md: 0 } }}>Сохраненные пресеты</Typography>
                            {presets.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">Пока нет сохраненных пресетов.</Typography>
                            ) : (
                                <Paper variant="outlined">
                                    <List disablePadding>
                                        {presets.map((preset, index) => (
                                            <React.Fragment key={preset.id}>
                                                <ListItem
                                                    secondaryAction={
                                                        <Tooltip title="Удалить пресет">
                                                            <IconButton edge="end" aria-label="delete" onClick={() => deletePreset(preset.id)} color="error">
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    }
                                                >
                                                    <ListItemText
                                                        primary={preset.name}
                                                        secondary={new Date(preset.createdAt).toLocaleDateString()}
                                                        onClick={() => loadPreset(preset)}
                                                        sx={{ cursor: 'pointer' }}
                                                    />
                                                </ListItem>
                                                {index < presets.length - 1 && <Divider />}
                                            </React.Fragment>
                                        ))}
                                    </List>
                                </Paper>
                            )}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            <ToastNotification toast={toast} onClose={handleClose} />
        </Box>
    );
};
